import React, { useState, useEffect } from 'react';

import cn from 'clsx';

import { uniqueId } from '@helpers/unuqueId';

import Autocomplete from './Autocomplete';
import InputSizes from './InputSizes';
import InputTypes from './InputTypes';
import InputWrapper from './InputWrapper';

let timer: ReturnType<typeof setTimeout>;

function getInputTagName(type: InputTypes = InputTypes.text) {
  switch (type) {
    case InputTypes.textarea:
      return 'textarea';
    case InputTypes.select:
      return 'select';
    default:
      return 'input';
  }
}

interface IPohInputPropsAutocompleteOptions {
  data: any;
  findFunction?: (query: any) => (i: any) => any;
  renderItem?: (props: any) => React.ReactElement<any>;
  allowCustom?: boolean;
  minLength?: number;
  debounce?: number;
}

export interface IPohInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onBlur' | 'onChange'> {
  id?: string;
  label?: React.ReactNode;
  message?: React.ReactNode;
  className?: string;
  labelClassName?: string;
  wrapperClassName?: string;
  value?: string;
  onRealChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: InputSizes;
  horizontal?: boolean;
  type?: InputTypes;
  autocompleteOpts?: IPohInputPropsAutocompleteOptions;
  labelGrid?: any;
  inputGrid?: any;
  min?: number | string;
  max?: number | string;
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  step?: number;
  name?: string;
  autoComplete?: string;
  placeholder?: string;
  readOnly?: boolean;
  ref?: React.RefObject<HTMLInputElement | null>;
  before?: React.ReactNode;
  after?: React.ReactNode;
}

const PohInput: React.FC<IPohInputProps> = (props) => {
  const {
    id,
    size,
    label = null,
    message = null,
    className,
    value = '',
    onChange,
    onRealChange,
    onBlur,
    autocompleteOpts,
    horizontal = false,
    labelGrid = 2,
    inputGrid = 10,
    labelClassName,
    type = InputTypes.text,
    disabled = false,
    autoFocus = false,
    wrapperClassName,
    ref,
    before,
    after,
    ...rest
  } = props;

  const [val, setVal] = useState(value ?? '');
  const uId = id || uniqueId(`poh-${type}`);

  const inpClassNames = cn('form-control', className, {
    'form-control-lg': size === InputSizes.Large,
    'form-control-sm': size === InputSizes.Small,
  });

  useEffect(() => {
    if (value === val) {
      return () => clearTimeout(timer);
    }

    setVal(value);
    return () => clearTimeout(timer);
  }, [value, val]);

  return (
    <InputWrapper
      id={uId}
      size={size}
      labelGrid={labelGrid}
      labelClassName={labelClassName}
      wrapperClassName={wrapperClassName}
      label={label}
      inputGrid={inputGrid}
      horizontal={horizontal}
      message={message}
    >
      {before}
      {type === InputTypes.autocomplete ? (
        <Autocomplete
          className={inpClassNames}
          id={uId}
          value={value}
          ref={ref}
          data={autocompleteOpts?.data}
          renderItem={autocompleteOpts?.renderItem as any}
          findFunction={autocompleteOpts?.findFunction}
          allowCustom={autocompleteOpts?.allowCustom}
          debounce={autocompleteOpts?.debounce}
          minLength={autocompleteOpts?.minLength}
          onChange={(event) => {
            clearTimeout(timer);
            timer = setTimeout(() => onRealChange?.(val ?? '', event), 300);
            setVal(event.target.value);
            onChange?.(event.target.value, event);
          }}
          onBlur={(event) => {
            clearTimeout(timer);
            onRealChange?.(event.target.value, event);
            onBlur?.(event);
          }}
          otherInputProps={rest}
        />
      ) : (
        React.createElement(getInputTagName(type), {
          className: inpClassNames,
          id: uId,
          ref,
          type,
          disabled: disabled,
          autoFocus,
          ...(typeof rest.defaultValue !== 'undefined'
            ? {}
            : {
                value: val,
                onChange: (event: any) => {
                  clearTimeout(timer);
                  timer = setTimeout(() => onRealChange?.(val ?? '', event), 300);
                  setVal(event.target.value);
                  onChange?.(event.target.value, event);
                },
              }),
          onBlur: (event: any) => {
            clearTimeout(timer);
            onRealChange?.(event.target.value, event);
            onBlur?.(event);
          },
          ...rest,
        })
      )}
      {after}
    </InputWrapper>
  );
};

export { InputTypes, InputSizes };

export default PohInput;
